import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  IconButton,
  Avatar,
  Chip,
  Card,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  CalendarToday,
  ChevronLeft,
  ChevronRight,
  VideoCall,
  Message,
  Check,
  Close,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import lawyerService from '../../services/lawyerService';
import { axelionColors } from '../../theme/axelionTheme';

const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];

const LawyerSchedulePage = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });
  const [events, setEvents] = useState({});
  const [loading, setLoading] = useState(true);

  const loadSchedule = useCallback(async () => {
    try {
      setLoading(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const data = await lawyerService.schedule.getSchedule(year, month);
      setEvents(data.events || {});
    } catch (error) {
      console.error('Error loading schedule:', error);
      setEvents({});
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    return { daysInMonth, startingDayOfWeek };
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleDateClick = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
  };

  const hasEvents = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events[dateStr]?.length > 0;
  };

  const handleConfirm = async (eventId) => {
    try {
      await lawyerService.schedule.confirmConsultation(eventId);
      toast.success('Консультация подтверждена');
      loadSchedule();
    } catch {
      toast.error('Ошибка подтверждения');
    }
  };

  const handleReject = async (eventId) => {
    try {
      await lawyerService.schedule.rejectConsultation(eventId);
      toast.info('Консультация отклонена');
      loadSchedule();
    } catch {
      toast.error('Ошибка отклонения');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: axelionColors.bgCream, pb: 4 }}>
      <Box
        sx={{
          background: axelionColors.bgLight,
          borderBottom: `1px solid ${axelionColors.borderLight}`,
          py: 3,
          px: 2,
          boxShadow: '0 2px 6px rgba(26, 26, 26, 0.06)',
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/lawyer/dashboard')}
              sx={{
                bgcolor: axelionColors.bgCream,
                color: axelionColors.textDark,
                border: `1px solid ${axelionColors.borderLight}`,
                '&:hover': { bgcolor: axelionColors.bgBeige },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ color: axelionColors.textDark }}
            >
              Календарь консультаций
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: axelionColors.gold }} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Card
                sx={{
                  p: 3,
                  background: axelionColors.bgLight,
                  border: `1px solid ${axelionColors.borderLight}`,
                  borderRadius: '8px',
                  boxShadow: '0 2px 6px rgba(26, 26, 26, 0.06)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <IconButton onClick={handlePrevMonth} sx={{ color: axelionColors.gold }}>
                    <ChevronLeft />
                  </IconButton>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: axelionColors.textDark }}>
                    {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </Typography>
                  <IconButton onClick={handleNextMonth} sx={{ color: axelionColors.gold }}>
                    <ChevronRight />
                  </IconButton>
                </Box>

                <Grid container spacing={1}>
                  {DAYS.map((day) => (
                    <Grid item xs={12 / 7} key={day}>
                      <Box sx={{ textAlign: 'center', py: 1 }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: axelionColors.textMuted }}>
                          {day}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}

                  {[...Array(startingDayOfWeek)].map((_, index) => (
                    <Grid item xs={12 / 7} key={`empty-${index}`}>
                      <Box sx={{ height: 80 }} />
                    </Grid>
                  ))}

                  {[...Array(daysInMonth)].map((_, index) => {
                    const day = index + 1;
                    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const isSelected = selectedDate === dateStr;
                    const hasEvent = hasEvents(day);
                    const isToday = new Date().toISOString().split('T')[0] === dateStr;

                    return (
                      <Grid item xs={12 / 7} key={day}>
                        <Box
                          onClick={() => handleDateClick(day)}
                          sx={{
                            height: 80,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 1,
                            cursor: 'pointer',
                            borderRadius: '8px',
                            border: isSelected
                              ? `2px solid ${axelionColors.gold}`
                              : `1px solid ${axelionColors.borderLight}`,
                            bgcolor: isSelected ? axelionColors.accentLight : axelionColors.bgLight,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: axelionColors.bgBeige,
                              borderColor: axelionColors.gold,
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight={isSelected || isToday ? 'bold' : 'normal'}
                            sx={{
                              color: isSelected ? axelionColors.gold : isToday ? axelionColors.bronze : axelionColors.textDark,
                            }}
                          >
                            {day}
                          </Typography>
                          {hasEvent && (
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: axelionColors.gold,
                                mt: 0.5,
                              }}
                            />
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Card
                sx={{
                  p: 3,
                  background: axelionColors.bgLight,
                  border: `1px solid ${axelionColors.borderLight}`,
                  borderRadius: '8px',
                  boxShadow: '0 2px 6px rgba(26, 26, 26, 0.06)',
                }}
              >
                <Typography variant="h6" fontWeight="bold" sx={{ color: axelionColors.textDark }} gutterBottom>
                  {selectedDate
                    ? `События на ${new Date(selectedDate).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}`
                    : 'Выберите дату'}
                </Typography>

                <Box sx={{ mt: 2 }}>
                  {selectedDate && events[selectedDate]?.length > 0 ? (
                    events[selectedDate].map((event) => (
                      <Card
                        key={event.id}
                        sx={{
                          mb: 2,
                          p: 2,
                          background: axelionColors.bgCream,
                          border: `1px solid ${axelionColors.borderLight}`,
                          borderRadius: '8px',
                          boxShadow: 'none',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: axelionColors.textDark }}>
                            {event.time}
                          </Typography>
                          <Chip
                            icon={event.type === 'video' ? <VideoCall /> : <Message />}
                            label={event.type === 'video' ? 'Видео' : 'Чат'}
                            size="small"
                            sx={{
                              bgcolor: event.type === 'video' ? axelionColors.gold : axelionColors.bronze,
                              color: 'white',
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: axelionColors.gold }}>
                            {event.client?.charAt(0) || 'К'}
                          </Avatar>
                          <Typography variant="body2" sx={{ color: axelionColors.textDark }}>
                            {event.client}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: axelionColors.textMuted, mb: 2 }}>
                          {event.topic}
                        </Typography>
                        {event.status === 'pending' && (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<Check />}
                              onClick={() => handleConfirm(event.id)}
                              sx={{
                                flex: 1,
                                bgcolor: axelionColors.success,
                                color: 'white',
                                borderRadius: '8px',
                                '&:hover': { bgcolor: '#16a34a' },
                              }}
                            >
                              Принять
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Close />}
                              onClick={() => handleReject(event.id)}
                              sx={{
                                flex: 1,
                                color: axelionColors.error,
                                borderColor: axelionColors.error,
                                borderRadius: '8px',
                                '&:hover': {
                                  bgcolor: axelionColors.errorLight,
                                  borderColor: axelionColors.error,
                                },
                              }}
                            >
                              Отклонить
                            </Button>
                          </Box>
                        )}
                        {(event.status === 'accepted' || event.status === 'confirmed') && (
                          <Chip
                            label="Подтверждено"
                            size="small"
                            sx={{
                              bgcolor: axelionColors.success,
                              color: 'white',
                            }}
                          />
                        )}
                      </Card>
                    ))
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CalendarToday sx={{ fontSize: 40, color: axelionColors.borderLight, mb: 1.5 }} />
                      <Typography variant="body1" sx={{ color: axelionColors.textDark, mb: 0.5 }}>
                        {selectedDate ? 'Нет консультаций' : 'Выберите дату'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                        {selectedDate ? 'На эту дату записей нет. Свободный день!' : 'Нажмите на дату в календаре для просмотра расписания'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default LawyerSchedulePage;
